/**
 * JobListPage Component
 *
 * Displays list of jobs for a specific batch with:
 * - Filtering by status, date range, search
 * - Sorting by status, created_at, completed_at
 * - Actions: Create Job, Import Jobs (bulk upload), Run All, Export
 *
 * Route: /batches/:batch_id/jobs
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useJobs, useCreateJob, useExecuteJob, useBatchExecuteJobs } from '../hooks/useJobs';
import { useBatch } from '../hooks/useBatches';
import { useAgents } from '../hooks/useAgents';
import { JobFilters, JobCreate } from '../types/job';
import { CreateJobForm } from '../components/jobs/CreateJobForm';
import toast from 'react-hot-toast';

export const JobListPage: React.FC = () => {
  const { batch_id } = useParams<{ batch_id: string }>();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<JobFilters>({
    batch_id: batch_id,
    sort_by: 'created_at',
    sort_order: 'desc',
  });

  const [showCreateJob, setShowCreateJob] = useState(false);

  // Fetch batch data for context
  const { data: batch } = useBatch(batch_id);

  // Fetch agents for the project (for Flow 1 agent selection)
  const { data: agentsData } = useAgents(batch ? { project_id: batch.project_id } : undefined);
  const agents = agentsData?.agents || [];

  // Fetch jobs with filters
  const { data, isLoading, error, refetch } = useJobs(filters);

  // Create job mutation
  const createJobMutation = useCreateJob();

  // Execute job mutations
  const executeJobMutation = useExecuteJob();
  const batchExecuteMutation = useBatchExecuteJobs();

  // Filter handlers for future filtering UI (Task Group 9)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFilterChange = (newFilters: Partial<JobFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClearFilters = () => {
    setFilters({
      batch_id: batch_id,
      sort_by: 'created_at',
      sort_order: 'desc',
    });
  };

  const handleCreateJob = async (jobData: JobCreate) => {
    try {
      await createJobMutation.mutateAsync(jobData);
      toast.success('Job created successfully');
      setShowCreateJob(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create job');
    }
  };

  const handleExecuteJob = async (jobId: string) => {
    try {
      await executeJobMutation.mutateAsync(jobId);
      toast.success('Job execution started');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to execute job');
    }
  };

  const handleRunAll = async () => {
    if (!batch_id) return;

    try {
      await batchExecuteMutation.mutateAsync(batch_id);
      toast.success('Batch execution started for all pending jobs');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to execute jobs');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Go back"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {batch?.name || 'Job List'}
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage jobs for this batch
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => toast('Bulk upload feature will be available soon')}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Import Jobs
              </button>
              <button
                onClick={() => setShowCreateJob(true)}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Create Job
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <svg
              className="animate-spin h-8 w-8 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading jobs
                </h3>
                <p className="mt-1 text-sm text-red-700">{error.message}</p>
                <button
                  onClick={() => refetch()}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && data && data.jobs.length === 0 && (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new job or importing from CSV/JSON.
            </p>
          </div>
        )}

        {/* Jobs Table Placeholder */}
        {!isLoading && !error && data && data.jobs.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  Jobs ({data.jobs.length})
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={handleRunAll}
                    disabled={batchExecuteMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {batchExecuteMutation.isPending ? 'Starting...' : 'Run All'}
                  </button>
                  <button
                    onClick={() => toast('Export feature coming soon')}
                    className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Export
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600">
                Job list table with filtering, sorting, and actions will be available after Task Group 9 components are complete.
              </p>

              {/* Simple list as placeholder */}
              <div className="space-y-2">
                {data.jobs.map((job) => (
                  <div
                    key={job.job_id}
                    className="border border-gray-200 rounded-md p-4 hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div
                        className="flex-1 cursor-pointer min-w-0"
                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                      >
                        <p className="text-sm font-medium text-gray-900 truncate" title={job.goal}>
                          {job.goal}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 truncate" title={job.website_url}>
                          {job.website_url}
                        </p>
                        {job.input_id && (
                          <p className="text-xs text-gray-400 mt-1 truncate" title={job.input_id}>
                            ID: {job.input_id}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          job.status === 'completed' ? 'bg-green-100 text-green-800' :
                          job.status === 'running' ? 'bg-blue-100 text-blue-800' :
                          job.status === 'run_failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {job.status}
                        </span>
                        {/* Allow running job in any status except running (each execution creates new session) */}
                        {job.status !== 'running' && (
                          <button
                            onClick={() => handleExecuteJob(job.job_id)}
                            disabled={executeJobMutation.isPending}
                            className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Run
                          </button>
                        )}
                        {job.status === 'running' && (
                          <div className="flex items-center gap-1 px-2 py-1 text-xs text-blue-700">
                            <div className="h-1.5 w-1.5 rounded-full bg-blue-600 animate-pulse"></div>
                            <span>Running</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Create Job Modal */}
      {showCreateJob && batch && (
        <CreateJobForm
          projectId={batch.project_id}
          batchId={batch_id!}
          agents={agents}
          showAgentSelection={false}
          onSubmit={handleCreateJob}
          onCancel={() => setShowCreateJob(false)}
          isLoading={createJobMutation.isPending}
        />
      )}
    </div>
  );
};
